import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly authUrl = 'https://aieventzo.com/admin_services/auth.php';
  private readonly platform = 'admin';
  private http: HttpClient;
  private sessionValidation$?: Observable<any>;
  private lastValidatedToken: string | null = null;
  private lastValidatedUserId: string | null = null;

  constructor(private httpBackend: HttpBackend) {
    this.http = new HttpClient(httpBackend);
  }

  getToken(): string | null {
    const localToken = localStorage.getItem('session_token') || localStorage.getItem('token');
    const sessionToken = sessionStorage.getItem('session_token') || sessionStorage.getItem('token');

    // If both exist and differ, manual modification in one of the storages has occurred.
    // Prioritize localStorage so if modified in DevTools it is immediately picked up and validated against the DB.
    if (localToken && sessionToken && localToken !== sessionToken) {
      return localToken;
    }

    return localToken || sessionToken;
  }

  getUserId(): string | null {
    const localUserId = localStorage.getItem('User_ID') || localStorage.getItem('user_id');
    const sessionUserId = sessionStorage.getItem('User_ID') || sessionStorage.getItem('user_id');

    if (localUserId && sessionUserId && localUserId !== sessionUserId) {
      return localUserId;
    }

    return localUserId || sessionUserId;
  }

  getRole(): string | null {
    return (
      localStorage.getItem('User') ||
      localStorage.getItem('role') ||
      sessionStorage.getItem('User') ||
      sessionStorage.getItem('role')
    );
  }

  isSuperAdmin(): boolean {
    return this.getRole() === 'superadmin';
  }

  storeSession(data: any) {
    const userId = `${data?.user_id ?? ''}`.trim();
    const sessionToken = `${data?.session_token ?? ''}`.trim();
    const role = `${data?.role ?? ''}`.trim();

    if (role) {
      sessionStorage.setItem('User', role);
      sessionStorage.setItem('role', role);
      localStorage.setItem('User', role);
      localStorage.setItem('role', role);
    }

    if (userId) {
      sessionStorage.setItem('User_ID', userId);
      sessionStorage.setItem('user_id', userId);
      localStorage.setItem('User_ID', userId);
      localStorage.setItem('user_id', userId);
    }

    if (sessionToken) {
      sessionStorage.setItem('session_token', sessionToken);
      sessionStorage.setItem('token', sessionToken);
      localStorage.setItem('session_token', sessionToken);
      localStorage.setItem('token', sessionToken);
    }

    this.lastValidatedToken = sessionToken;
    this.lastValidatedUserId = userId;
  }

  logout() {
    sessionStorage.removeItem('session_token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('User_ID');
    sessionStorage.removeItem('user_id');
    sessionStorage.removeItem('User');
    sessionStorage.removeItem('role');
    sessionStorage.clear();

    localStorage.removeItem('session_token');
    localStorage.removeItem('token');
    localStorage.removeItem('User_ID');
    localStorage.removeItem('user_id');
    localStorage.removeItem('User');
    localStorage.removeItem('role');

    this.sessionValidation$ = undefined;
    this.lastValidatedToken = null;
    this.lastValidatedUserId = null;
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    const userId = this.getUserId();
    return !!token && !!userId && token.trim().length > 0 && userId.trim().length > 0;
  }

  validateSession(): Observable<any> {
    if (!this.isLoggedIn()) {
      return throwError(() => new HttpErrorResponse({
        status: 401,
        statusText: 'Unauthorized',
        error: {
          flag: false,
          msg: 'User is not logged in'
        }
      }));
    }

    const currentToken = this.getToken();
    const currentUserId = this.getUserId();

    // If token or user_id changed in storage, invalidate any existing session check
    if (this.lastValidatedToken !== currentToken || this.lastValidatedUserId !== currentUserId) {
      this.sessionValidation$ = undefined;
    }

    if (this.sessionValidation$) {
      return this.sessionValidation$;
    }

    const body = {
      user_id: currentUserId,
      session_token: currentToken,
      platform: this.platform
    };

    this.sessionValidation$ = this.http.post<any>(this.authUrl, body).pipe(
      tap({
        next: (res) => {
          if (res && res.flag === true) {
            this.lastValidatedToken = currentToken;
            this.lastValidatedUserId = currentUserId;
          } else {
            this.lastValidatedToken = null;
            this.lastValidatedUserId = null;
          }
        },
        error: () => {
          this.lastValidatedToken = null;
          this.lastValidatedUserId = null;
        }
      }),
      shareReplay(1),
      finalize(() => {
        this.sessionValidation$ = undefined;
      })
    );

    return this.sessionValidation$;
  }

  checkStatus() {
    if (!this.isLoggedIn()) {
      return;
    }

    this.validateSession().subscribe({
      next: (res: any) => {
        console.log('Status checked:', res?.msg);
      },
      error: (err) => {
        console.error('Session check failed', err);
      }
    });
  }
}
